import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, AdminClient } from '../api/admin';
import {
  AdminBoardConfig, AvailableField, AvailableSection, getAdminConfig, getAvailableFields,
  getAvailableSections, putAdminConfig,
} from '../api/asanaBoard';
import { applyFieldMode, AdminFieldMode, intakePlacement } from './asanaBoardConfigRules';
import { copyBoardSettings, CopyResult } from './copyBoardSettings';

const EMPTY: AdminBoardConfig = {
  workspaceGid: '', projectGid: '', visibilityFieldGid: '',
  viewOptionGid: '', discussOptionGid: '',
  epicFieldGid: null, epicOptionGid: null,
  displayName: 'Project Delivery Board', commentsEnabled: true, showFieldHistory: false,
  boardGroupFieldGid: null,
  taskCreationEnabled: false, intakeSectionGid: null,
  requestTypeFieldGid: null, requestTypeQuestionOptionGid: null,
  requestTypes: [],
  fields: [],
};

type Mode = AdminFieldMode;

// The picker's sentinel for "type a GID instead". Not a valid GID.
const PASTE = '__paste__';

// The keys below are the nullable ones on AdminBoardConfig — everything else
// is a required string, and clearing it must keep '' (not null) so validation
// reports "required" instead of the server's opaque
// "Expected string, received null".
const NULLABLE_KEYS = new Set<keyof AdminBoardConfig>([
  'epicFieldGid', 'epicOptionGid', 'boardGroupFieldGid',
  'intakeSectionGid', 'requestTypeFieldGid', 'requestTypeQuestionOptionGid',
]);

export default function AdminModuleAsanaBoard() {
  const { clientId } = useParams();
  const { getToken } = useAuth();
  const [cfg, setCfg] = useState<AdminBoardConfig>(EMPTY);
  const [status, setStatus] = useState<string | null>(null);
  const [available, setAvailable] = useState<AvailableField[] | null>(null);
  // Which client this is. The screen is reachable by direct URL, so it is
  // fetched rather than passed in — arriving here without knowing whose board
  // you are editing is how the wrong project GID gets saved.
  const [client, setClient] = useState<AdminClient | null>(null);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [loadingFields, setLoadingFields] = useState(false);
  // What the entered project GID actually resolves to in Asana.
  const [projectName, setProjectName] = useState<string | null>(null);
  // The project's sections in board order, for the intake picker. Null until
  // loaded or when Asana could not be read — the pasted-GID box covers both.
  const [sections, setSections] = useState<AvailableSection[] | null>(null);
  // Staff chose "paste a GID" from the picker.
  const [pasteSection, setPasteSection] = useState(false);
  // Copy settings from another client. `others` is every other client; `copy`
  // is the previewed result, held until the admin fills the form or cancels.
  const [others, setOthers] = useState<AdminClient[]>([]);
  const [copySourceId, setCopySourceId] = useState('');
  const [copy, setCopy] = useState<{ from: string; result: CopyResult } | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    (async () => {
      const token = await getToken();
      const [r, c] = await Promise.all([
        getAdminConfig(clientId, token),
        adminApi.getClient(token, clientId),
      ]);
      if (r.config) setCfg(r.config);
      setClient(c);
      // Best-effort: without the list the copy panel just has nothing to offer.
      adminApi.listClients(token).then((all) => setOthers(all.filter((x) => x.id !== clientId))).catch(() => {});
    })().catch((e) => setStatus(String(e)));
  }, [clientId, getToken]);

  // Field discovery is keyed off the project GID typed above, so it can run
  // before anything has been saved.
  const loadFields = useCallback(async (projectGid: string) => {
    if (!clientId || !projectGid.trim()) return;
    setLoadingFields(true);
    setFieldsError(null);
    try {
      const r = await getAvailableFields(clientId, projectGid.trim(), await getToken());
      setAvailable(r.fields);
      setProjectName(r.projectName);
      // Best-effort and separate: the picker degrades to a pasted GID, so a
      // sections failure must not take the field list down with it.
      getAvailableSections(clientId, projectGid.trim(), await getToken())
        .then((s) => setSections(s.sections))
        .catch(() => setSections(null));
    } catch {
      setAvailable(null);
      setProjectName(null);
      setSections(null);
      setFieldsError(
        'Could not read that project from Asana. Check the project GID, and that the portal’s '
        + 'Asana user has access to it.',
      );
    } finally {
      setLoadingFields(false);
    }
  }, [clientId, getToken]);

  // Auto-load once when an existing config arrives, so the screen opens ready
  // to edit rather than needing a click first.
  useEffect(() => {
    if (cfg.projectGid && available === null && !loadingFields && !fieldsError) {
      loadFields(cfg.projectGid);
    }
  }, [cfg.projectGid, available, loadingFields, fieldsError, loadFields]);

  const previewCopy = async () => {
    const from = others.find((c) => c.id === copySourceId);
    if (!from || !available) return;
    setCopyBusy(true);
    setCopy(null);
    setCopyMessage(null);
    try {
      const r = await getAdminConfig(from.id, await getToken());
      if (!r.config) {
        setCopyMessage(`${from.name} has no board configured yet, so there is nothing to copy.`);
        return;
      }
      setCopy({ from: from.name, result: copyBoardSettings(cfg, r.config, available) });
    } catch {
      setCopyMessage(`Could not read ${from.name}’s board settings.`);
    } finally {
      setCopyBusy(false);
    }
  };

  const fillFromCopy = () => {
    if (!copy) return;
    setCfg(copy.result.next);
    // The intake picker's "paste" mode belongs to the old value.
    setPasteSection(false);
    setCopyMessage(`Filled from ${copy.from}. Review the form, then Save — nothing is saved yet.`);
    setCopy(null);
  };

  const set = <K extends keyof AdminBoardConfig>(k: K, v: AdminBoardConfig[K]) =>
    setCfg((c) => ({ ...c, [k]: v }));

  const setTextField = (key: keyof AdminBoardConfig, value: string) =>
    setCfg((c) => ({ ...c, [key]: value || (NULLABLE_KEYS.has(key) ? null : '') }));

  const byGid = useMemo(
    () => new Map((available ?? []).map((f) => [f.fieldGid, f])),
    [available],
  );

  // Chosen fields, in display order. Anything in the saved config that the
  // project no longer offers is kept rather than dropped — silently discarding
  // it on the next save would change what clients see without anyone deciding
  // to. It's shown flagged instead.
  const chosen = cfg.fields;

  const modeOf = (fieldGid: string): Mode =>
    chosen.find((f) => f.fieldGid === fieldGid)?.mode ?? 'hidden';

  const setMode = (field: AvailableField, mode: Mode) =>
    setCfg((c) => applyFieldMode(c, field.fieldGid, mode, field.name));

  const relabel = (fieldGid: string, label: string) =>
    setCfg((c) => ({ ...c, fields: c.fields.map((f) => (f.fieldGid === fieldGid ? { ...f, label } : f)) }));

  const move = (fieldGid: string, delta: number) =>
    setCfg((c) => {
      const i = c.fields.findIndex((f) => f.fieldGid === fieldGid);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= c.fields.length) return c;
      const next = [...c.fields];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...c, fields: next.map((f, k) => ({ ...f, sortOrder: k })) };
    });

  // Enum options for the visibility / epic pickers, sourced from discovery so
  // staff choose a name instead of pasting an option GID.
  const optionsFor = (fieldGid: string | null) =>
    (fieldGid ? byGid.get(fieldGid)?.options : undefined) ?? [];

  // ---- the types a client may pick when raising an item --------------------
  //
  // Offered options, in the order the dialog renders them. Undefined on a
  // config saved before request types existed; treated as none offered, and
  // left undefined in state until something here changes it, so merely
  // opening this screen and saving does not rewrite a client's taxonomy.
  const requestTypes = cfg.requestTypes ?? [];

  const setRequestTypes = (next: Array<{ optionGid: string; label: string; sortOrder: number }>) =>
    set('requestTypes', next.map((t, i) => ({ ...t, sortOrder: i })));

  const offerType = (optionGid: string, name: string) =>
    // Seeded with the Asana option's own name, which is right often enough to
    // be a sensible default and is editable in place when it isn't.
    setRequestTypes([...requestTypes, { optionGid, label: name, sortOrder: 0 }]);

  const withdrawType = (optionGid: string) =>
    setRequestTypes(requestTypes.filter((t) => t.optionGid !== optionGid));

  const relabelType = (optionGid: string, label: string) =>
    setRequestTypes(requestTypes.map((t) => (t.optionGid === optionGid ? { ...t, label } : t)));

  const moveType = (optionGid: string, delta: number) => {
    const i = requestTypes.findIndex((t) => t.optionGid === optionGid);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= requestTypes.length) return;
    const next = [...requestTypes];
    [next[i], next[j]] = [next[j], next[i]];
    setRequestTypes(next);
  };

  const requestTypeOptions = optionsFor(cfg.requestTypeFieldGid);

  // An offered option the Request Type field no longer has — the field GID was
  // changed, or the option was deleted or is on another field entirely. Shown
  // rather than dropped: the client is being offered a radio that would be
  // refused on submit, and clearing it silently would hide that.
  const staleRequestTypes = requestTypes.filter(
    (t) => requestTypeOptions.length > 0 && !requestTypeOptions.some((o) => o.gid === t.optionGid),
  );

  const intakeHit = sections?.find((x) => x.gid === cfg.intakeSectionGid);
  const placement = intakePlacement(sections, cfg.intakeSectionGid);

  const enumFields = (available ?? []).filter((f) => f.subtype === 'enum');

  // Same filter the grouping select renders its options from — only a field
  // the client can already see. Pulled out so the select, the stale-value
  // note beside it, and save() all read from one list and cannot disagree.
  const groupFieldOptions = enumFields.filter((f) => {
    const row = cfg.fields.find((x) => x.fieldGid === f.fieldGid);
    return !!row && row.mode !== 'hidden';
  });

  // The stored grouping gid isn't among today's options — the project GID
  // changed, or the field was renamed/deleted/hidden in Asana since this was
  // last saved. Left alone, the select falls back to displaying "Asana
  // section (default)" while state still holds the old gid, and Save would
  // silently re-persist it. Surfaced instead of cleared on load, so nothing
  // changes what's live for the client without this screen saying so.
  const staleGroupFieldGid =
    !!cfg.boardGroupFieldGid && !groupFieldOptions.some((f) => f.fieldGid === cfg.boardGroupFieldGid);

  const save = async () => {
    setStatus('Saving…');
    try {
      // A stale grouping gid is cleared here rather than carried through:
      // the note beside the select promises this happens on save, and the
      // select was never able to honour it as a live selection anyway.
      const cleaned = staleGroupFieldGid ? { ...cfg, boardGroupFieldGid: null } : cfg;
      // Renumber sortOrder from array position rather than trusting whatever
      // was last stamped: position is what staff arranged here, and it's the
      // one source of truth for order. Duplicate or gapped values sort
      // non-deterministically and nothing downstream catches it (zod only
      // checks int().min(0); there's no DB uniqueness constraint), so the
      // client's board order could silently diverge from this screen.
      const payload = { ...cleaned, fields: cleaned.fields.map((f, i) => ({ ...f, sortOrder: i })) };
      await putAdminConfig(clientId!, payload, await getToken());
      if (staleGroupFieldGid) setCfg((c) => ({ ...c, boardGroupFieldGid: null }));
      setStatus('Saved');
    } catch (e) {
      setStatus(String(e));
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      {/* Whose board this is, and the way back. Everything on this screen
          decides what one specific firm can see, so the firm is named at the
          top rather than left implicit in the URL. */}
      <nav style={{ fontSize: 13, marginBottom: 8 }}>
        <Link to="/admin/clients">Clients</Link>
        <span style={{ color: 'var(--mi-muted)', margin: '0 6px' }}>/</span>
        <Link to={`/admin/clients/${clientId}${client?.slug ? `?slug=${client.slug}` : ''}`}>
          {client?.name ?? 'Client'}
        </Link>
        <span style={{ color: 'var(--mi-muted)', margin: '0 6px' }}>/</span>
        <span style={{ color: 'var(--mi-muted)' }}>Project Delivery Board</span>
      </nav>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: 'var(--mi-navy)' }}>
          Project Delivery Board — {client?.name ?? '…'}
        </h2>
        {client?.slug && <code style={{ fontSize: 13, color: 'var(--mi-muted)' }}>{client.slug}</code>}
      </div>

      <p style={{ color: '#666' }}>
        Every field defaults to hidden. A field is visible to <strong>{client?.name ?? 'this client'}</strong>{' '}
        only when set to Read or Edit here <em>and</em> the task is flagged in Asana.
      </p>

      <h3>Connection</h3>
      {([
        ['workspaceGid', 'Workspace GID'],
        ['projectGid', 'Project GID'],
        ['displayName', 'Sidebar label'],
      ] as Array<[keyof AdminBoardConfig, string]>).map(([key, label]) => (
        <div key={key} style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 13 }}>{label}</label>
          <input
            style={{ width: '100%', padding: 6 }}
            value={(cfg[key] as string | null) ?? ''}
            onChange={(e) => setTextField(key, e.target.value)}
            onBlur={key === 'projectGid' ? (e) => loadFields(e.target.value) : undefined}
          />
        </div>
      ))}

      {/* The project GID is the isolation boundary: point a client at another
          client's project and they see that client's flagged tasks. Naming
          what the GID resolves to turns a silent typo into a visible one. */}
      {projectName && (
        <div style={{
          background: '#eef6ee', border: '1px solid #cfe3cf', borderRadius: 6,
          padding: '8px 12px', margin: '4px 0 10px', fontSize: 13,
        }}>
          That project GID is <strong>{projectName}</strong>.
          {client?.name && !projectName.toLowerCase().includes((client.name.split(' ')[0] ?? '').toLowerCase()) && (
            <div style={{ color: '#8a4a0b', marginTop: 4 }}>
              Heads up — that name doesn’t obviously match {client.name}. Check it before saving.
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={() => loadFields(cfg.projectGid)} disabled={!cfg.projectGid.trim() || loadingFields}>
        {loadingFields ? 'Loading fields…' : available ? 'Reload fields from Asana' : 'Load fields from Asana'}
      </button>
      {fieldsError && (
        <div style={{ background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginTop: 8, fontSize: 13 }}>
          {fieldsError}
        </div>
      )}

      {/* Copy another client's setup into this form. Fills the form only:
          saving still goes through the normal PUT and its validation. Needs
          the project's fields loaded, because every GID is checked against
          them before it is copied. */}
      <h3 style={{ marginTop: 24 }}>Copy settings from another client</h3>
      {!available ? (
        <p style={{ color: '#666', fontSize: 13 }}>
          Load the project’s fields above first — each setting is checked against this project before it is copied.
        </p>
      ) : (
        <>
          <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
            Fills this form with another client’s task creation, request types, field visibility and
            modes, and grouping. It never copies the project, workspace or intake section — those are
            specific to this board. Anything not on this project is skipped and listed.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              aria-label="Copy from client"
              value={copySourceId}
              onChange={(e) => { setCopySourceId(e.target.value); setCopy(null); setCopyMessage(null); }}
              style={{ padding: 6, minWidth: 240 }}
            >
              <option value="">— choose a client —</option>
              {others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button type="button" onClick={previewCopy} disabled={!copySourceId || copyBusy}>
              {copyBusy ? 'Reading…' : 'Preview changes'}
            </button>
          </div>
          {copyMessage && <p style={{ fontSize: 13, color: '#333' }}>{copyMessage}</p>}
          {copy && (
            <div style={{ border: '1px solid var(--mi-border)', borderRadius: 6, padding: 12, marginTop: 8 }}>
              <strong style={{ fontSize: 13 }}>Copying from {copy.from}</strong>
              {copy.result.changes.length === 0 ? (
                <p style={{ fontSize: 13, color: '#666' }}>This form already matches — nothing would change.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', fontSize: 12, color: '#666' }}>
                      <th style={{ padding: '4px 8px 4px 0' }}>Setting</th>
                      <th style={{ padding: '4px 8px' }}>Now</th>
                      <th style={{ padding: '4px 8px' }}>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {copy.result.changes.map((c) => (
                      <tr key={c.label} style={{ borderTop: '1px solid #eee', fontSize: 13 }}>
                        <td style={{ padding: '4px 8px 4px 0' }}>{c.label}</td>
                        <td style={{ padding: '4px 8px', color: '#666' }}>{c.before}</td>
                        <td style={{ padding: '4px 8px' }}>{c.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {copy.result.skipped.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#8a4a0b' }}>
                  Skipped ({copy.result.skipped.length}):
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {copy.result.skipped.map((k) => <li key={`${k.setting}|${k.reason}`}>{k.setting} — {k.reason}</li>)}
                  </ul>
                </div>
              )}
              {copy.result.notes.length > 0 && (
                <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13, color: '#333' }}>
                  {copy.result.notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
              <div style={{ marginTop: 12 }}>
                <button type="button" onClick={fillFromCopy} disabled={copy.result.changes.length === 0}>
                  Fill the form
                </button>{' '}
                <button type="button" onClick={() => setCopy(null)}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}

      <h3 style={{ marginTop: 24 }}>Visibility</h3>
      {available ? (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13 }}>Client Visibility field</label>
            <select
              style={{ width: '100%', padding: 6 }}
              value={cfg.visibilityFieldGid}
              onChange={(e) => setCfg((c) => ({
                ...c,
                visibilityFieldGid: e.target.value,
                // The old option GIDs belong to the previous field and would
                // silently never match, so they are cleared with it.
                viewOptionGid: '',
                discussOptionGid: '',
              }))}
            >
              <option value="">— select —</option>
              {enumFields.map((f) => <option key={f.fieldGid} value={f.fieldGid}>{f.name}</option>)}
            </select>
          </div>

          {([
            ['viewOptionGid', 'Option meaning “client can view”'],
            ['discussOptionGid', 'Option meaning “client can view and comment”'],
          ] as Array<[keyof AdminBoardConfig, string]>).map(([key, label]) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 13 }}>{label}</label>
              <select
                style={{ width: '100%', padding: 6 }}
                value={(cfg[key] as string) ?? ''}
                onChange={(e) => set(key, e.target.value as AdminBoardConfig[typeof key])}
                disabled={!cfg.visibilityFieldGid}
              >
                <option value="">— select —</option>
                {optionsFor(cfg.visibilityFieldGid).map((o) => (
                  <option key={o.gid} value={o.gid}>{o.name}</option>
                ))}
              </select>
            </div>
          ))}

          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13 }}>
              Epic field (optional — promotes epics to section headers)
            </label>
            <select
              style={{ width: '100%', padding: 6 }}
              value={cfg.epicFieldGid ?? ''}
              onChange={(e) => setCfg((c) => ({
                ...c,
                epicFieldGid: e.target.value || null,
                epicOptionGid: null,
              }))}
            >
              <option value="">— none —</option>
              {enumFields.map((f) => <option key={f.fieldGid} value={f.fieldGid}>{f.name}</option>)}
            </select>
          </div>

          {cfg.epicFieldGid && (
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 13 }}>Option meaning “this is an epic”</label>
              <select
                style={{ width: '100%', padding: 6 }}
                value={cfg.epicOptionGid ?? ''}
                onChange={(e) => set('epicOptionGid', e.target.value || null)}
              >
                <option value="">— select —</option>
                {optionsFor(cfg.epicFieldGid).map((o) => (
                  <option key={o.gid} value={o.gid}>{o.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13 }}>
              Group the Board view by
            </label>
            <select
              style={{ width: '100%', padding: 6 }}
              value={cfg.boardGroupFieldGid ?? ''}
              onChange={(e) => set('boardGroupFieldGid', e.target.value || null)}
            >
              <option value="">Asana section (default)</option>
              {groupFieldOptions.map((f) => <option key={f.fieldGid} value={f.fieldGid}>{f.name}</option>)}
            </select>
            <span style={{ display: 'block', color: '#666', fontSize: 12, marginTop: 4 }}>
              Only fields set to Read or Edit below can be chosen — the column headers show every
              option the field has. Pick a field with few options and client-legible values: a
              15-option status field renders as 16 columns, most of them empty.
            </span>
            {staleGroupFieldGid && (
              <span style={{ display: 'block', color: '#8a4a0b', fontSize: 12, marginTop: 4 }}>
                The field this board was grouped by is no longer available (renamed, deleted, or
                hidden in Asana). It shows as “Asana section (default)” above, and saving will
                clear the stored value to match.
              </span>
            )}
          </div>
        </>
      ) : (
        <p style={{ color: '#666', fontSize: 13 }}>
          Load the project’s fields above to choose the visibility and epic fields by name.
        </p>
      )}

      <label style={{ display: 'block', margin: '12px 0' }}>
        <input
          type="checkbox"
          checked={cfg.commentsEnabled}
          onChange={(e) => set('commentsEnabled', e.target.checked)}
        />{' '}
        Allow client comments (per-task “Client: Discuss” still required)
      </label>

      <label style={{ display: 'block', margin: '12px 0' }}>
        <input
          type="checkbox"
          checked={cfg.showFieldHistory}
          onChange={(e) => set('showFieldHistory', e.target.checked)}
        />{' '}
        Show a “Task updates” log on each item
        <span style={{ display: 'block', color: '#666', fontSize: 12, marginLeft: 22 }}>
          A dated record of field changes made through the portal, shown in its own
          section below the comments. Off by default.
        </span>
      </label>

      <h3 style={{ marginTop: 24 }}>Letting this client raise items</h3>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
        A raised item is created in Asana stamped <strong>Client: Discuss</strong> and, if a
        Request Type field is set below, <strong>Question</strong>. It arrives unassigned, in the
        intake section, for the delivery team to triage.
      </p>

      <label style={{ display: 'block', margin: '12px 0' }}>
        <input
          type="checkbox"
          checked={cfg.taskCreationEnabled}
          onChange={(e) => set('taskCreationEnabled', e.target.checked)}
        />{' '}
        Allow this client to raise a new item
        <span style={{ display: 'block', color: '#666', fontSize: 12, marginLeft: 22 }}>
          Requires an intake section below — saving without one is refused, because the write
          path would then have to guess where a raised item goes.
        </span>
      </label>

      <div style={{ margin: '12px 0' }}>
        <label htmlFor="intake-section">Intake section</label>
        {sections && (
          <select
            id="intake-section"
            value={pasteSection || (!!cfg.intakeSectionGid && !intakeHit) ? PASTE : (cfg.intakeSectionGid ?? '')}
            onChange={(e) => {
              if (e.target.value === PASTE) { setPasteSection(true); return; }
              setPasteSection(false);
              set('intakeSectionGid', e.target.value || null);
            }}
            style={{ display: 'block', width: '100%', maxWidth: 420, marginTop: 4 }}
          >
            <option value="">— none —</option>
            {sections.map((s) => (
              <option key={s.gid} value={s.gid}>
                {s.position}. {s.name} ({s.gid}){s.isLast ? ' — last' : ''}
              </option>
            ))}
            <option value={PASTE}>Other — paste a section GID…</option>
          </select>
        )}
        {(!sections || pasteSection || (!!cfg.intakeSectionGid && !intakeHit)) && (
          <input
            aria-label="Intake section GID"
            id={sections ? undefined : 'intake-section'}
            value={cfg.intakeSectionGid ?? ''}
            onChange={(e) => setTextField('intakeSectionGid', e.target.value)}
            placeholder="Section GID every raised item lands in"
            style={{ display: 'block', width: '100%', maxWidth: 420, marginTop: 4 }}
          />
        )}
        {placement.kind === 'last' && (
          <span style={{ display: 'block', color: '#2a6b2a', fontSize: 12, marginTop: 4 }}>
            Section {placement.position} of {placement.total} — last on the board, after the delivery sections.
          </span>
        )}
        {placement.kind === 'not-last' && (
          <span style={{ display: 'block', color: '#8a4a0b', fontSize: 12, marginTop: 4 }}>
            Section {placement.position} of {placement.total} — not the last section. Intake normally
            sits after the delivery sections so the work reads first and the triage queue last.
            Move it in Asana, or pick the last section.
          </span>
        )}
        {placement.kind === 'unlisted' && sections && (
          <span style={{ display: 'block', color: '#b00', fontSize: 12, marginTop: 4 }}>
            That GID is not a section of this project. Saving is refused until it is.
          </span>
        )}
        {!sections && (
          <span style={{ display: 'block', color: '#666', fontSize: 12, marginTop: 4 }}>
            The project’s sections are not loaded — load the fields above to pick one by name.
          </span>
        )}
        <span style={{ display: 'block', color: '#666', fontSize: 12 }}>
          One section, not the section the client happened to be looking at — so client requests
          land in a single triage queue instead of scattered through the epics.
        </span>
      </div>

      <label style={{ display: 'block', margin: '12px 0' }}>
        Request Type field GID
        <input
          value={cfg.requestTypeFieldGid ?? ''}
          onChange={(e) => setTextField('requestTypeFieldGid', e.target.value)}
          style={{ display: 'block', width: '100%', maxWidth: 420, marginTop: 4 }}
        />
      </label>

      <label style={{ display: 'block', margin: '12px 0' }}>
        Request Type “Question” option GID
        <input
          value={cfg.requestTypeQuestionOptionGid ?? ''}
          onChange={(e) => setTextField('requestTypeQuestionOptionGid', e.target.value)}
          style={{ display: 'block', width: '100%', maxWidth: 420, marginTop: 4 }}
        />
        <span style={{ display: 'block', color: '#666', fontSize: 12 }}>
          Set both or neither. A field with no option to write, or an option with no field to
          write it on, is half a stamp and is refused on save.
        </span>
      </label>

      <h4 style={{ marginTop: 20, marginBottom: 4 }}>Types this client can choose between</h4>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
        Offer some of the Request Type field’s options and the Raise an item dialog asks which
        kind of item it is, as radio buttons with nothing preselected. Offer none and it asks for
        a summary and detail only, stamping the option above on everything. The label is what the
        client reads and need not match Asana — <em>Defect</em> is how we classify it,
        “Issue or bug” is the phrase someone testing reaches for.
      </p>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
        Offer fewer than the field has. An option that describes how MI classifies its own work
        is not a distinction the client is making when they file something, and a commercially
        loaded one is a read to make with them rather than through a radio button.
      </p>

      {!cfg.requestTypeFieldGid ? (
        <p style={{ color: '#666', fontSize: 13 }}>
          Set a Request Type field above first — the options come from it.
        </p>
      ) : requestTypeOptions.length === 0 ? (
        <p style={{ color: '#666', fontSize: 13 }}>
          Load the project’s fields above to list that field’s options.
        </p>
      ) : (
        <>
          {requestTypes.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: 12, color: '#666' }}>
                  <th style={{ padding: '4px 8px 4px 0' }}>Shown to the client</th>
                  <th style={{ padding: '4px 8px' }}>Asana option</th>
                  <th style={{ padding: '4px 8px', width: 120 }}>Order</th>
                  <th style={{ padding: '4px 0', width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {requestTypes.map((t, i) => {
                  const opt = requestTypeOptions.find((o) => o.gid === t.optionGid);
                  return (
                    <tr key={t.optionGid} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '6px 8px 6px 0' }}>
                        <input
                          value={t.label}
                          onChange={(e) => relabelType(t.optionGid, e.target.value)}
                          style={{ width: '100%', maxWidth: 260 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: 13, color: opt ? '#333' : '#b00' }}>
                        {opt ? opt.name : `${t.optionGid} — not on this field`}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <button type="button" onClick={() => moveType(t.optionGid, -1)} disabled={i === 0}>
                          ↑
                        </button>{' '}
                        <button
                          type="button"
                          onClick={() => moveType(t.optionGid, 1)}
                          disabled={i === requestTypes.length - 1}
                        >
                          ↓
                        </button>
                      </td>
                      <td style={{ padding: '6px 0' }}>
                        <button type="button" onClick={() => withdrawType(t.optionGid)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {staleRequestTypes.length > 0 && (
            <p style={{ color: '#b00', fontSize: 12 }}>
              {staleRequestTypes.length === 1 ? 'One offered type is' : `${staleRequestTypes.length} offered types are`}{' '}
              not an option on this Request Type field. The client is shown the radio and the API
              refuses it on submit. Remove it, or point the field GID above at the right field.
            </p>
          )}

          <div style={{ fontSize: 13 }}>
            {requestTypeOptions.filter((o) => !requestTypes.some((t) => t.optionGid === o.gid)).length === 0 ? (
              <span style={{ color: '#666' }}>Every option on this field is offered.</span>
            ) : (
              <>
                <span style={{ color: '#666' }}>Not offered: </span>
                {requestTypeOptions
                  .filter((o) => !requestTypes.some((t) => t.optionGid === o.gid))
                  .map((o) => (
                    <button
                      key={o.gid}
                      type="button"
                      onClick={() => offerType(o.gid, o.name)}
                      style={{ marginRight: 6, marginBottom: 6 }}
                    >
                      + {o.name}
                    </button>
                  ))}
              </>
            )}
          </div>

          {requestTypes.length > 0
            && !!cfg.requestTypeQuestionOptionGid
            && !requestTypes.some((t) => t.optionGid === cfg.requestTypeQuestionOptionGid) && (
            <p style={{ color: '#666', fontSize: 12, marginBottom: 0 }}>
              The fallback option above is not one of these. Items raised without a type — a
              browser on an older bundle, say — are classified as something no client could have
              chosen. Usually worth offering it too.
            </p>
          )}
        </>
      )}

      <p style={{ color: '#666', fontSize: 12, marginTop: 20, marginBottom: 0 }}>
        The Timeline view appears for this client only when both <strong>Start date</strong> and{' '}
        <strong>Due date</strong> below are set to Read or Edit. A bar’s position on the chart is
        its dates, so it cannot be shown while a date is hidden.
      </p>

      <h3 style={{ marginTop: 24 }}>Fields shown to the client</h3>
      {!available ? (
        <p style={{ color: '#666', fontSize: 13 }}>Load the project’s fields above to choose which to show.</p>
      ) : (
        <>
          {chosen.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ fontSize: 12, color: '#666' }}>
                  <th align="left" style={{ width: 60 }}>Order</th>
                  <th align="left">Field</th>
                  <th align="left">Column heading shown to client</th>
                  <th align="left" style={{ width: 110 }}>Mode</th>
                </tr>
              </thead>
              <tbody>
                {chosen.map((f, i) => {
                  const meta = byGid.get(f.fieldGid);
                  return (
                    <tr key={f.fieldGid} style={{ borderTop: '1px solid var(--mi-border)' }}>
                      <td>
                        <button type="button" onClick={() => move(f.fieldGid, -1)} disabled={i === 0}
                                aria-label="Move up" style={miniBtn}>↑</button>
                        <button type="button" onClick={() => move(f.fieldGid, 1)} disabled={i === chosen.length - 1}
                                aria-label="Move down" style={miniBtn}>↓</button>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {meta?.name ?? f.label}
                        {!meta && (
                          // Kept, not dropped: this field is live for clients
                          // right now, and removing it should be a decision.
                          <span style={{ color: '#900', marginLeft: 8, fontSize: 12 }}>
                            no longer on this project
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          value={f.label}
                          onChange={(e) => relabel(f.fieldGid, e.target.value)}
                          style={{ width: '100%', padding: 4 }}
                        />
                      </td>
                      <td>
                        <select
                          value={f.mode}
                          onChange={(e) => meta
                            ? setMode(meta, e.target.value as Mode)
                            // Same applyFieldMode() as setMode, for a field
                            // that's no longer on the project (no `meta`, so
                            // no default label to seed with — the row always
                            // already exists here).
                            : setCfg((c) => applyFieldMode(c, f.fieldGid, e.target.value as Mode))}
                        >
                          <option value="read">Read</option>
                          {(meta?.editable ?? true) && <option value="edit">Edit</option>}
                          <option value="hidden">Remove</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>Available on this project</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {available.filter((f) => modeOf(f.fieldGid) === 'hidden').map((f) => (
                <tr key={f.fieldGid} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={{ fontSize: 13, padding: '4px 0' }}>
                    {f.name}
                    <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                      {f.builtin ? 'built-in' : f.subtype}
                    </span>
                  </td>
                  <td style={{ width: 200, textAlign: 'right' }}>
                    <button type="button" onClick={() => setMode(f, 'read')} style={miniBtn}>Show as read</button>
                    {f.editable && (
                      <button type="button" onClick={() => setMode(f, 'edit')} style={miniBtn}>Allow edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div style={{ marginTop: 24 }}>
        <button onClick={save}>Save</button>
        {status && <span style={{ marginLeft: 12 }}>{status}</span>}
      </div>
    </div>
  );
}

const miniBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)', borderRadius: 4,
  padding: '2px 8px', fontSize: 12, marginLeft: 4, cursor: 'pointer',
};
